import type React from "react";

import {
  BUSINESS_NAME,
  EMAIL,
  getFormattedAddress,
} from "@/businessInfo/business";

const LAST_UPDATED = "November 7, 2025";

const PrivacyPolicyPage: React.FC = () => {
  const mailingAddress = getFormattedAddress();

  return (
    <main className="py-16 bg-slate-50">
      <div className="max-w-4xl px-4 mx-auto sm:px-6 lg:px-8">
        <header className="space-y-4">
          <p className="text-sm font-semibold tracking-wide uppercase text-slate-500">
            Last updated {LAST_UPDATED}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Privacy Policy
          </h1>
          <p className="text-base leading-relaxed text-slate-700">
            This privacy policy explains how {BUSINESS_NAME} (&quot;we,&quot;
            &quot;our,&quot; or &quot;us&quot;) collects, uses, and safeguards
            personal information across our digital properties. Our work is
            rooted in community partnership across Washington, D.C. By using our
            website, subscribing to updates, donating, or engaging with our
            programs, you agree to the practices described below.
          </p>
        </header>

        <section className="mt-12 space-y-8">
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Information We Collect
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We collect information that helps us deliver community programs,
              communicate opportunities, and steward donor relationships. This
              may include:
            </p>
            <ul className="pl-6 space-y-2 text-base leading-relaxed list-disc text-slate-700">
              <li>
                Contact information such as your name, email address, phone
                number, and mailing address when you sign up for updates,
                volunteer, or request services.
              </li>
              <li>
                Donation and sponsorship details needed to process contributions
                and provide receipts.
              </li>
              <li>
                Program participation preferences, including interests in weekly
                food distributions, the Weekend Backpack Program, Thanksgiving
                Baskets, transitional housing, neighborhood clean teams, facade
                improvements, and other community services.
              </li>
              <li>
                Professional and business details shared when accessing small
                business support offerings or workshops delivered with partner
                organizations.
              </li>
              <li>
                Communications you send to us, including questions about housing
                counseling with Manna Community Development Corporation (MCT),
                immigration services delivered with United Methodist Just
                Neighbors, or other requests for assistance.
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              How We Use Information
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              Personal information helps us coordinate meaningful support with
              mission-driven partners. We use the information described above
              to:
            </p>
            <ul className="pl-6 space-y-2 text-base leading-relaxed list-disc text-slate-700">
              <li>
                Deliver services and resources requested by families and
                individuals.
              </li>
              <li>
                Schedule volunteer shifts and communicate opportunities to
                serve.
              </li>
              <li>
                Administer events such as holiday basket distributions and
                community walks.
              </li>
              <li>
                Process donations, issue acknowledgements, and report impact.
              </li>
              <li>
                Coordinate housing counseling, immigration services, small
                business support, and other specialized programs with trusted
                collaborators.
              </li>
              <li>
                Improve our website, outreach, and strategic planning
                activities.
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Community Programs Supported by Our Partners
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We collaborate with mission-aligned organizations to sustain a
              human-centered response across Washington, D.C. Information shared
              with us helps advance:
            </p>
            <ul className="pl-6 space-y-2 text-base leading-relaxed list-disc text-slate-700">
              <li>
                Food access efforts, including weekly pantry distributions,
                Thanksgiving Baskets, the Weekend Backpack Program, and ongoing
                community meals.
              </li>
              <li>
                Housing supports such as transitional housing, relocation
                assistance, homeowner education, and foreclosure mitigation
                offered with MCT.
              </li>
              <li>
                Economic empowerment services including small business
                consulting, free website creation, grant application help, and
                neighborhood facade improvements.
              </li>
              <li>
                Community revitalization initiatives anchored by mixed-use
                development projects, neighborhood clean teams, and corridor
                beautification efforts.
              </li>
              <li>
                Immigration legal services offered through United Methodist Just
                Neighbors, providing free assistance to low-income residents.
              </li>
              <li>
                Community engagement moments such as the annual Walk for the
                Homeless and donor-led campaigns that sustain more than 1,900
                families each year.
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              When We Share Information
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We do not sell personal information. We may share information when
              it is necessary to fulfill the purposes outlined in this policy,
              including with:
            </p>
            <ul className="pl-6 space-y-2 text-base leading-relaxed list-disc text-slate-700">
              <li>
                Trusted partners such as MCT, United Methodist Just Neighbors,
                The Community Builders, and other vetted collaborators that
                deliver specific services with us.
              </li>
              <li>
                Vendors and technology providers that support our
                communications, fundraising, and program coordination, under
                confidentiality obligations.
              </li>
              <li>
                Government agencies or legal authorities when required to comply
                with law, regulations, or valid legal process.
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Data Retention, Security, and International Use
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We retain personal information only as long as necessary to
              advance our mission or comply with legal obligations. We implement
              reasonable safeguards—including secure systems, limited access,
              and staff training—to protect your information. Our operations are
              focused in the United States, and by engaging with us you consent
              to the transfer and processing of your data within the U.S.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Your Choices
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              You can update your contact information, request a copy of the
              data we hold about you, or ask us to delete or restrict the use of
              your personal information by emailing us at {EMAIL}. You may opt
              out of marketing communications by using the unsubscribe link in
              our emails or contacting us directly. We will respond to requests
              within a reasonable timeframe.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Children&apos;s Information
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We do not knowingly collect personal information from children
              under the age of 13 without verifiable parental or guardian
              consent. If you believe a child has provided us with personal
              information, please contact us so we can take appropriate action.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Updates to This Policy
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We may update this privacy policy to reflect evolving programs,
              regulatory requirements, or operational needs. When changes are
              material, we will post a notice on this page with the revised
              effective date. Continued use of our site or services after
              changes become effective constitutes acceptance of the revised
              policy.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              Contact Us
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              Questions or privacy requests can be sent to {EMAIL} or by mail to{" "}
              {BUSINESS_NAME}, {mailingAddress}. We appreciate your partnership
              in creating a healthy, economically empowered Brightwood
              community.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
